import { useId, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppLayout } from '../../components/templates/AppLayout/AppLayout';
import { Header } from '../../components/layout/Header';
import { Sidebar } from '../../components/Sidebar';
import { PageContent } from '../../components/templates/PageContent';
import { PageHeader } from '../../components/templates/PageHeader';
import { Section } from '../../components/templates/Section';
import { Card } from '../../components/atoms/Card';
import { Stack } from '../../components/atoms/Stack';
import { Inline } from '../../components/atoms/Inline';
import { Button } from '../../components/atoms/Button';
import { Text } from '../../components/atoms/Typography';
import { Alert } from '../../components/atoms/Alert';
import { FieldError } from '../../components/atoms/FieldError/FieldError';
import { LiveRegion } from '../../components/atoms/LiveRegion/LiveRegion';
import { PhotoUploadDropzone } from '../../components/atoms/PhotoUploadDropzone/PhotoUploadDropzone';
import { AiLimitErrorMessage } from '../../components/aiUsage/AiLimitNotice';
import { useTranslation } from '../../i18n/useTranslation';
import { ZeovitModuleGate } from '../../components/zeovit/ZeovitModuleGate';
import { ZeovitUnavailableNotice } from '../../components/zeovit/ZeovitUnavailableNotice';
import { ZeovitInputsNotice } from '../../components/zeovit/ZeovitInputsNotice';
import { ZeovitConcernsFieldset } from '../../components/zeovit/ZeovitConcernsFieldset';
import { ZeovitVolumeRequiredNotice } from '../../components/zeovit/ZeovitVolumeRequiredNotice';
import { SUPPLY_SYSTEM_NOT_ZEOVIT } from '../../utils/supplySystem';
import { ApiError } from '../../lib/apiClient';
import { uploadGalleryImages } from '../../services/galleryService';
import { getAiLimitErrorKind } from '../../services/aiUsageService';
import { CHECK_RATE_LIMITED_CODE } from '../../services/zeovitRecommendationService';
import { AQUARIUM_VOLUME_REQUIRED_CODE, type ZeovitConcern } from '../../constants/zeovitConcerns';
import { useRunZeovitCheck } from '../../hooks/useZeovitRecommendations';

/** What went wrong with the last submit: the backend rate limit, an AI limit, or anything else. */
type SubmitFailure =
  | { kind: 'tooSoon' }
  | { kind: 'aiLimit'; error: unknown }
  | { kind: 'notZeovit' }
  | { kind: 'volume' }
  | { kind: 'generic' };

type Phase = 'form' | 'submitting' | 'failed';

function classifyFailure(error: unknown): SubmitFailure {
  if (error instanceof ApiError && error.status === 429 && error.code === CHECK_RATE_LIMITED_CODE) {
    return { kind: 'tooSoon' };
  }
  if (error instanceof ApiError && error.status === 409 && error.code === SUPPLY_SYSTEM_NOT_ZEOVIT) {
    return { kind: 'notZeovit' };
  }
  if (error instanceof ApiError && error.status === 409 && error.code === AQUARIUM_VOLUME_REQUIRED_CODE) {
    return { kind: 'volume' };
  }
  if (getAiLimitErrorKind(error) !== null) return { kind: 'aiLimit', error };
  return { kind: 'generic' };
}

function PeriodicCheckPageContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { aquariumId, recommendationId } = useParams<{ aquariumId: string; recommendationId: string }>();
  const runCheck = useRunZeovitCheck();

  const photoLabelId = useId();
  const photoErrorId = useId();
  const dropzoneRef = useRef<HTMLDivElement>(null);

  const [phase, setPhase] = useState<Phase>('form');
  const [failure, setFailure] = useState<SubmitFailure | null>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoMissing, setPhotoMissing] = useState(false);
  // Empty → the observations of the current recommendation carry over (server side).
  const [concerns, setConcerns] = useState<ZeovitConcern[]>([]);
  const [concernNote, setConcernNote] = useState('');
  // "Continue anyway" on the update request: not asked again in this flow (kept here,
  // so it survives the form being re-mounted after a failed submit).
  const [inputsAcknowledged, setInputsAcknowledged] = useState(false);
  // The uploaded photo's key, so a retry does not upload the same file again.
  const uploaded = useRef<{ file: File; key: string } | null>(null);

  const detailPath =
    aquariumId && recommendationId
      ? `/aquariums/${encodeURIComponent(aquariumId)}/zeovit-recommendations/${encodeURIComponent(recommendationId)}`
      : null;

  const handleCancel = () => {
    if (detailPath) navigate(detailPath);
    else navigate(-1);
  };

  const handlePhoto = (file: File) => {
    setPhoto(file);
    setPhotoMissing(false);
  };

  const uploadPhoto = async (aquarium: string, file: File): Promise<string> => {
    if (uploaded.current?.file === file) return uploaded.current.key;
    const [image] = await uploadGalleryImages(aquarium, [file]);
    if (!image?.s3Key) throw new Error('The uploaded photo has no storage key');
    uploaded.current = { file, key: image.s3Key };
    return image.s3Key;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!aquariumId || !recommendationId || !detailPath) return;

    setPhotoMissing(!photo);
    if (!photo) {
      dropzoneRef.current?.focus();
      return;
    }

    setFailure(null);
    setPhase('submitting');
    try {
      const newPhotoKey = await uploadPhoto(aquariumId, photo);
      // The server compares the aquarium's current stored measurements with the reference.
      const note = concernNote.trim();
      await runCheck.mutateAsync({
        aquariumId,
        recommendationId,
        newPhotoKey,
        ...(concerns.length > 0 ? { concerns } : {}),
        ...(note ? { concernNote: note } : {}),
      });
      navigate(`${detailPath}/check-result`, { state: { focusHeading: true } });
    } catch (error: unknown) {
      setFailure(classifyFailure(error));
      setPhase('failed');
    }
  };

  const cancelAction = (
    <Button variant="secondary" onClick={handleCancel}>
      {t('zeovit.check.cancel')}
    </Button>
  );

  let body;
  if (phase === 'submitting') {
    body = (
      <Card>
        <Text color="muted">{t('zeovit.check.analyzing')}</Text>
      </Card>
    );
  } else if (phase === 'failed' && failure) {
    body = (
      <Stack gap="4">
        {failure.kind === 'tooSoon' ? (
          <Alert tone="warning">
            <Text>{t('zeovit.check.error.tooSoon')}</Text>
          </Alert>
        ) : failure.kind === 'aiLimit' ? (
          <AiLimitErrorMessage error={failure.error} />
        ) : failure.kind === 'notZeovit' ? (
          aquariumId ? <ZeovitUnavailableNotice aquariumId={aquariumId} /> : null
        ) : failure.kind === 'volume' ? (
          aquariumId ? <ZeovitVolumeRequiredNotice aquariumId={aquariumId} /> : null
        ) : (
          <Alert tone="danger">
            <Stack gap="2">
              <Text>{t('zeovit.check.error.generic')}</Text>
              <Inline>
                <Button variant="secondary" onClick={() => setPhase('form')}>
                  {t('zeovit.check.retry')}
                </Button>
              </Inline>
            </Stack>
          </Alert>
        )}
      </Stack>
    );
  } else {
    body = (
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <Stack gap="8">
          <Section title={t('zeovit.check.photoSection')}>
            <Card>
              <Stack gap="4">
                <div role="group" aria-labelledby={photoLabelId}>
                  <Stack gap="2">
                    <Text id={photoLabelId} size="sm" weight="medium">
                      {t('zeovit.check.photoLabel')}
                    </Text>
                    <PhotoUploadDropzone
                      ref={dropzoneRef}
                      onChange={handlePhoto}
                      maxSizeMB={10}
                      ariaLabel={t('zeovit.check.photoLabel')}
                      // On a missing photo the dropzone receives focus — link the error to it.
                      ariaDescribedBy={photoMissing ? photoErrorId : undefined}
                    />
                    {photoMissing ? (
                      <FieldError id={photoErrorId}>{t('zeovit.check.error.photoRequired')}</FieldError>
                    ) : null}
                  </Stack>
                </div>
              </Stack>
            </Card>
          </Section>

          <ZeovitConcernsFieldset
            concerns={concerns}
            note={concernNote}
            onConcernsChange={setConcerns}
            onNoteChange={setConcernNote}
            description={t('zeovit.concerns.checkDescription')}
          />

          {aquariumId ? (
            <ZeovitInputsNotice
              aquariumId={aquariumId}
              acknowledged={inputsAcknowledged}
              onContinue={() => setInputsAcknowledged(true)}
            />
          ) : null}

          <Inline>
            <Button type="submit" variant="primary">
              {t('zeovit.check.submit')}
            </Button>
          </Inline>
        </Stack>
      </form>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="narrow">
          <PageHeader
            title={t('zeovit.check.title')}
            description={phase === 'form' ? t('zeovit.check.description') : undefined}
            actions={phase === 'submitting' ? undefined : cancelAction}
          />
          <LiveRegion visuallyHidden busy={phase === 'submitting'}>
            {phase === 'submitting' ? t('zeovit.check.analyzing') : ''}
          </LiveRegion>
          {body}
        </PageContent>
      }
    />
  );
}

/**
 * Only for aquariums whose supply system is ZeoVit (#1784): otherwise the page shows
 * the info notice instead (ZeovitModuleGate).
 */
export function PeriodicCheckPage() {
  const { t } = useTranslation();
  const { aquariumId } = useParams<{ aquariumId: string }>();
  return (
    <ZeovitModuleGate aquariumId={aquariumId} title={t('zeovit.check.title')} width="narrow">
      <PeriodicCheckPageContent />
    </ZeovitModuleGate>
  );
}

export default PeriodicCheckPage;
